import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

const CANVAS_HEIGHT = 140;
const MIN_CANVAS_WIDTH = 280;

function drawImageOnCanvas(
  canvas: HTMLCanvasElement,
  imageDataUrl: string | null,
  width: number,
  options: {
    backgroundColor: string;
    strokeColor: string;
    // Guards image.onload against an out-of-order draw repainting a cleared
    // canvas with a prior signature.
    isStale: () => boolean;
  }
) {
  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  const devicePixelRatio = window.devicePixelRatio || 1;

  canvas.width = width * devicePixelRatio;
  canvas.height = CANVAS_HEIGHT * devicePixelRatio;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${CANVAS_HEIGHT}px`;

  context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
  context.clearRect(0, 0, width, CANVAS_HEIGHT);
  context.fillStyle = options.backgroundColor;
  context.fillRect(0, 0, width, CANVAS_HEIGHT);
  context.strokeStyle = options.strokeColor;
  context.lineCap = 'round';
  context.lineJoin = 'round';
  context.lineWidth = 2.5;

  if (!imageDataUrl) {
    return;
  }

  const image = new Image();
  image.onload = () => {
    if (options.isStale()) {
      return;
    }
    const imageScale = Math.min(
      width / image.width,
      CANVAS_HEIGHT / image.height
    );
    const renderedWidth = image.width * imageScale;
    const renderedHeight = image.height * imageScale;
    const renderedX = (width - renderedWidth) / 2;
    const renderedY = (CANVAS_HEIGHT - renderedHeight) / 2;

    context.drawImage(
      image,
      renderedX,
      renderedY,
      renderedWidth,
      renderedHeight
    );
  };
  image.src = imageDataUrl;
}

export function PublicSignerSignatureCanvas({
  error,
  helperText = 'Draw your signature in the box.',
  onChange,
  value
}: {
  error?: string;
  helperText?: string;
  onChange: (value: string | null) => void;
  value: string | null;
}) {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawingSurfaceRef = useRef<HTMLDivElement>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const drawSeqRef = useRef(0);
  const [canvasWidth, setCanvasWidth] = useState(MIN_CANVAS_WIDTH);

  const syncCanvas = useCallback(
    (imageDataUrl: string | null) => {
      if (!canvasRef.current) {
        return;
      }

      const seq = ++drawSeqRef.current;
      drawImageOnCanvas(canvasRef.current, imageDataUrl, canvasWidth, {
        backgroundColor: theme.palette.background.paper,
        strokeColor: theme.palette.secondary.main,
        isStale: () => seq !== drawSeqRef.current
      });
    },
    [canvasWidth, theme.palette.background.paper, theme.palette.secondary.main]
  );

  useEffect(() => {
    if (!drawingSurfaceRef.current) {
      return;
    }

    const updateCanvasWidth = () => {
      if (!drawingSurfaceRef.current) {
        return;
      }

      setCanvasWidth(
        Math.max(drawingSurfaceRef.current.clientWidth, MIN_CANVAS_WIDTH)
      );
    };

    updateCanvasWidth();

    const observer = new ResizeObserver(() => {
      updateCanvasWidth();
    });

    observer.observe(drawingSurfaceRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    syncCanvas(value);
  }, [syncCanvas, value]);

  const getCanvasPoint = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();

      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    },
    []
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const context = canvas?.getContext('2d');

      if (!canvas || !context) {
        return;
      }

      event.currentTarget.setPointerCapture(event.pointerId);

      const point = getCanvasPoint(event);

      isDrawingRef.current = true;
      lastPointRef.current = point;

      context.beginPath();
      context.arc(point.x, point.y, 1.25, 0, 2 * Math.PI);
      context.fillStyle = theme.palette.secondary.main;
      context.fill();
    },
    [getCanvasPoint, theme.palette.secondary.main]
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current || !canvasRef.current) {
        return;
      }

      const context = canvasRef.current.getContext('2d');

      if (!context || !lastPointRef.current) {
        return;
      }

      const point = getCanvasPoint(event);

      context.beginPath();
      context.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      context.lineTo(point.x, point.y);
      context.stroke();

      lastPointRef.current = point;
    },
    [getCanvasPoint]
  );

  const handlePointerUp = useCallback(() => {
    if (!isDrawingRef.current || !canvasRef.current) {
      return;
    }

    isDrawingRef.current = false;
    lastPointRef.current = null;
    onChange(canvasRef.current.toDataURL('image/png'));
  }, [onChange]);

  return (
    <Stack spacing={1}>
      <Stack
        spacing={1}
        sx={(theme) => ({
          backgroundColor: alpha(theme.palette.background.paper, 0.98),
          border: `1px solid ${
            error ? theme.palette.error.main : theme.palette.divider
          }`,
          borderRadius: '16px',
          p: 2
        })}
      >
        <Box ref={drawingSurfaceRef} sx={{ width: '100%' }}>
          <canvas
            onPointerCancel={handlePointerUp}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            ref={canvasRef}
            style={{
              backgroundColor: theme.palette.background.paper,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
              borderRadius: '12px',
              boxSizing: 'border-box',
              cursor: 'crosshair',
              display: 'block',
              maxWidth: '100%',
              touchAction: 'none',
              width: '100%'
            }}
          />
        </Box>
        <Typography color="text.secondary" variant="body2">
          {helperText}
        </Typography>
      </Stack>
      {error ? (
        <Typography color="error.main" variant="caption">
          {error}
        </Typography>
      ) : null}
    </Stack>
  );
}
